import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue'
import api from '@/api'
import { mdiClose, mdiCrownOutline } from '@mdi/js'
import { IBattle } from '@/types/battle'
import { IBracketParticipant, IBrackets } from '@/components/Battle/Brackets/types'

interface IProps {
  brackets: IBrackets
  battle: IBattle
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BattleBracketsEditor',
  props: {
    brackets: null,
    battle: null
  },
  emits: ["update:brackets", "cancel"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:brackets', brackets: IBrackets): void
  (e: 'cancel'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  brackets: IBrackets
  battle: IBattle
};




const brackets = ref(JSON.parse(JSON.stringify(props.brackets)))
const isShowError = ref(false)
const loading = ref(false)

const currentLeftBracket = computed(() => {
  let index = brackets.value.left.findIndex((round) => round.some((match) => match.winner === 0))
  index = index === -1 ? brackets.value.left.length - 1 : index
  return { participants: brackets.value.left[index], round: index }
})

const currentRightBracket = computed(() => {
  let index = brackets.value.right.findIndex((round) => round.some((match) => match.winner === 0))
  index = index === -1 ? brackets.value.left.length - 1 : index
  return { participants: brackets.value.right[index], round: index }
})

const isFinalRound = computed(
  () => !!(brackets.value.final.red.id && brackets.value.final.blue.id) || props.battle.bracketSize === 2
)
const currentBrackets = computed(() => {
  const isSameRound = currentLeftBracket.value.round === currentRightBracket.value.round
  if (isFinalRound.value) {
    return {
      left: [],
      right: [],
      final: brackets.value.final,
    }
  }
  return {
    left: isSameRound
      ? currentLeftBracket.value.participants
      : brackets.value.left[Math.min(currentLeftBracket.value.round, currentRightBracket.value.round)],
    right: isSameRound
      ? currentRightBracket.value.participants
      : brackets.value.right[Math.min(currentLeftBracket.value.round, currentRightBracket.value.round)],
    final: [],
  }
})

const participants = computed(() => {
  const participantsList: IBracketParticipant[] = [{ name: '', id: 0, isDisabled: false }]

  const source = props.battle.crewSize > 1 ? props.battle.crews : props.battle.participants
  const sparrings = [...(currentBrackets.value.left || []), ...(currentBrackets.value.right || [])]

  const additionalParticipants = source.map((item) => {
    const isInSparringWithWinner = sparrings.some(
      (sparring) => (sparring.red.id === item.id || sparring.blue.id === item.id) && !!sparring.winner
    )

    if ('name' in item) {
      // item is ICrew
      return {
        id: item.id,
        name: item.name,
        isDisabled: !!item.place || isInSparringWithWinner,
      }
    }
    // item is IBracketParticipant
    return {
      id: item.id,
      name: item.dancerName,
      isDisabled: !!item.place || isInSparringWithWinner,
    }
  })

  participantsList.push(...additionalParticipants)

  return participantsList
})

const duplicatedParticipant = ref<number>()

const checkUniqueSparrings = (sparringList: { sparringId: number; red: number; blue: number }[]) => {
  const seenIds = new Set<number>()

  return sparringList.every(({ red, blue }) => {
    const isDuplicatedInSparring = red && blue && red === blue
    if (isDuplicatedInSparring) {
      duplicatedParticipant.value = red
      return false
    }

    // Check if red participant is duplicated in different sparrings
    const isRedDuplicated = red && seenIds.has(red)
    if (isRedDuplicated) {
      duplicatedParticipant.value = red
      return false
    }

    //  Check if blue participant is duplicated in different sparrings
    const isBlueDuplicated = blue && seenIds.has(blue)
    if (isBlueDuplicated) {
      duplicatedParticipant.value = blue
      return false
    }

    if (red) seenIds.add(red)
    if (blue) seenIds.add(blue)
    return true
  })
}

const updateSeed = async () => {
  loading.value = true
  const sparrings = [...currentBrackets.value.left, ...currentBrackets.value.right, currentBrackets.value.final]
    .filter((sparring) => sparring.winner === 0)
    .map((sparring) => {
      return {
        sparringId: sparring.sparringId,
        red: sparring.red.id,
        blue: sparring.blue.id,
      }
    })

  const isUniqueObjects = checkUniqueSparrings(sparrings)
  if (!isUniqueObjects) {
    isShowError.value = true
    loading.value = false
    return
  }

  isShowError.value = false

  const updatedBrackets = await api.battles.seedParticipants(props.battle.id, sparrings)
  if (!updatedBrackets) {
    loading.value = false
    return
  }

  emit('update:brackets', updatedBrackets)
  emit('cancel')
  loading.value = false
}

return { __sfc: true,props, emit, brackets, isShowError, loading, currentLeftBracket, currentRightBracket, isFinalRound, currentBrackets, participants, duplicatedParticipant, checkUniqueSparrings, updateSeed, mdiClose, mdiCrownOutline }
}

})